import { IRoomState } from './IRoomState';
import { IPlayerState } from './IPlayerState';
import { ITournamentExtraInformation } from './ITournamentExtraInformation';

export interface ISitOptions {
    seatIndex: number;
    buyInLimits: {
        min: number;
        max: number;
    };
    gameState: {
        isGameActive: boolean;
        requiresChoice: boolean;
        waitForBB?: boolean;
        postOrWaitOptions?: {
            canPost: boolean;
            postAmount: number;
            postType: string;
            blinds: {
                small: number;
                big: number;
            };
        };
        balanceWarning?: {
            type: 'mustReload' | 'shouldReload' | 'notEnoughBalance';
            currentBalance: number;
            requiredAmount?: number;
            recommendedAmount?: number;
            willSitOut: boolean;
            limits: {
                minBuyIn: number;
                maxBuyIn: number;
            };
        };
    };
    stoodAt?: string;
    normalBuyinResetAt?: string;
    timeUntilNormalBuyinResets?: number;
    currency?: string;
    currencyType?: string;
    exchangeRate?: number;
    rebuyOffer?: {
        tournamentId: string;
        chips: number;
        price: number;
        secondsToExpire: number;
        expiresAt?: string;
    } | null;
    addonOffer?: {
        tournamentId: string;
        endsAt: string;
        price: number;
        chips: number;
        maxPerPlayer: number;
    } | null;
}

interface ActivityMessage {
    template: string;
    data: {
        timestamp: string;
        username?: string;
        action?: string;
        amount?: number;
        currency?: string;
        [key: string]: unknown;
    };
}

export type GameplayEventKind =
    | 'handStarted'
    | 'playerAction'
    | 'turnStarted'
    | 'streetDealt'
    | 'showdownStarted'
    | 'showdownCardsRevealed'
    | 'winnerDeclared'
    | 'handEnded'
    | 'timerWarning';

export type GameplayEventStreet = 'flop' | 'turn' | 'river';
export type AutoActionIntent = 'checkOrFold' | 'fold' | 'call' | 'callAny' | 'allIn';

export interface IGameplayEventWinner {
    userId: string;
    username?: string;
    winAmount?: number;
    handName?: string;
    handRank?: unknown;
    winningCards?: string[];
    cards?: string[];
}

export interface IGameplayEventRevealedPlayer {
    userId: string;
    username?: string;
    cards: string[];
    revealOrder?: number;
    revealGroup?: number;
}

export interface IGameplayEvent {
    eventId: string;
    sequence: number;
    roomId: string;
    gameId: string;
    handId: string;
    kind: GameplayEventKind;
    serverTime: string;
    actorUserId?: string;
    actorUsername?: string;
    action?: string;
    amount?: number;
    roundName?: string;
    street?: GameplayEventStreet;
    cards?: string[];
    cardsCount?: number;
    communityCardsCount?: number;
    players?: IGameplayEventRevealedPlayer[];
    winnerIds?: string[];
    winners?: IGameplayEventWinner[];
    nonWinners?: unknown;
    potDistribution?: unknown;
    byDefault?: boolean;
    foldWin?: boolean;
}

export interface IJoinedRoomState extends IRoomState {
    gameId: string;
    handId?: string;
    stateVersion?: number;
    lastAppliedGameplaySequence?: number;
    lastGameplayEventSequence?: number;
    lastGameplayEventId?: string;
    lastWinnerDeclaredEvent?: IGameplayEvent;
    lastWinnerDeclaredReceivedAt?: number;
    serverTime?: string;
    turnId?: string;
    actionRequestId?: string;
    activityMessages: ActivityMessage[];
    playerSeatIndex: number | null;
    pot: number | null;
    currentBet: number | null;
    communityCards: string[];
    joinedAt: string | null;
    stateName: string | null | 'dealing' | 'preFlop' | 'flop' | 'turn' | 'river';
    roundName: string | null;
    dealerId: string | null;
    showSitFailedNoMoneyAlert: boolean;
    players: IPlayerState[];
    potBeforeDistribution: number | null;
    deck: string[],
    winners: {
        username: string;
        userId: string;
        handValue: number;
        handName: string;
        winAmount: number;
        cards: string[];
        handRank: {
            handName: string;
            winningCards: string[];
            pot: number;
            wonAmount: number;
        };
    }[] | [];
    nonWinners: {
        username: string;
        userId: string;
        handValue: number;
        handName: string;
        winAmount: number;
        cards: string[];
        handRank: {
            handName: string;
            winningCards: string[];
        };
    }[] | [];
    showdown: boolean,
    playersActedThisRound: number,
    shouldEndRound: boolean,
    smallBlindId: string | null,
    bigBlindId: string | null,
    shouldShowdown: boolean,
    spectators: Record<string, IPlayerState>;
    initialized: boolean;
    chatMessages: { message: string, user: string }[];
    activePlayerId: string | null;
    secondsUntilNextGame: number;
    secondsBeforeRemovingSitOutPlayers: number;
    availableActions: {
        userId: string;
        actions: Array<{
            type: string;
            minAmount: number;
            maxAmount: number;
        }>;
        turnId?: string;
        actionRequestId?: string;
        gameId?: string;
        handId?: string;
    } | null;
    playerTurnHistory: {
        userId: string;
        username: string;
        time: string;
        availableActions: any[];
        roundName: string;
        pot: number;
        communityCards: string[];
        playerCurrentBets: { userId: string; username: string; amount: number }[];
        playerBalances: { userId: string; username: string; amount: number }[];
        playerCards: { userId: string; username: string; cards: string[] }[];
        smallBlindId: string;
        bigBlindId: string;
        smallBlindUsername: string;
        bigBlindUsername: string;
        dealerId: string;
        dealerUsername: string;
        selectedAction: {
            action: string;
            amount: number;
        };
    }[];
    consecutiveGamesCount: number;
    roundingDust: number;
    turnTime: number;
    currentTurnStartTime: string | null;
    playerSatCount: number;
    showSitOptionsModal: boolean;
    showLeaveSeatDialog: boolean;
    showLeaveRoomDialog: boolean;
    sitOptions: ISitOptions | null;
    gameType: string;
    type: string;
    sidePots: any[];
    winnersPotDistribution: {
        userId: string;
        username: string;
        totalAmount: number;
        mainPotAmount: number;
        sidePotAmounts: {
            amount: number;
            potIndex: number;
            isExcessReturn: boolean;
        }[];
        cards: string[];
        handRank?: any;
    }[];
    ante: number;
    showCardsOptions?: {
        canShow: boolean;
        isMuckEnabled: boolean;
        opponents: string[];
        cards: string[];
    };
    showdownCards?: Array<{
        userId?: string;
        username?: string;
        show: boolean;
        cards: string[];
    }>;
    pendingBalanceUpdates: Array<{
        playerId: string;
        amount: number;
    }> | null;
    topUpNextHandPlayers: any;
    topUpAllHandsPlayers: any;
    willSitOutNextHandPlayers: any;
    autoActionIntent?: AutoActionIntent | null;
    autoActionRoundName?: string | null;
    isGameInfoExpanded: boolean;
    playerEquities: any;
    gameStartingIn?: number | null;
    currency: string;
    currencyType: 'fiat' | 'crypto' | 'tournament_chips';
    exchangeCurrencyValue: number;
    stakeLevel: string;
    visiblePot: number;
    currentGameId: string;
    eligiblePotPlayers: any[];
    visibleSidePots: any[];
    gameId_?: string;
    willSitOutNextBbPlayers: any;
    playersWaitingForBB: any;
    tournamentId?: string;
    tournamentGameId?: string;
    isPausedForTableOperation: boolean;
    isTableClosed: boolean;
    isTournamentStarting: boolean;
    addonEnabled?: boolean;
    addonEndsAt?: string | null;
    rebuyOffer?: {
        tournamentId: string;
        chips: number;
        price: number;
        secondsToExpire: number;
        expiresAt?: string;
    } | null;
    addonOffer?: {
        tournamentId: string;
        endsAt: string;
        price: number;
        chips: number;
        maxPerPlayer: number;
    } | null;
    showTournamentWinModal?: boolean;
    tournamentEndedInfo?: {
        tournamentId?: string;
        roomId?: string;
        winnerInformation?: {
            id: string;
            username: string;
            country?: string;
            chips?: number;
            prize?: number;
        };
        rankingData?: Array<{
            id: string;
            username: string;
            balance: number;
            position: number;
            prize: number;
            country?: string;
        }>;
        closeLastTableUponWinSeconds?: number;
    };
    tournamentCloseAt?: number; // epoch ms when the table should auto-close
    tournamentExtraInformation?: ITournamentExtraInformation;
    prizePool?: number;
    guaranteedPrizePool?: number;
    pendingNotification?: {
        message: string;
        type: string;
        timestamp: number;
    } | null;
    /**
     * Where this room join originated from:
     * - 'lobby': user initiated (joinSuccessful)
     * - 'server_request': server-initiated (joinInClient)
     */
    joinSource?: 'lobby' | 'server_request';

    // MTT-only: derived from tournament rankings/lobby state.
    // Used to decide whether closing the table should "dock" or "leave".
    tournamentEliminated?: boolean;
    tournamentEliminationTime?: string | null;
    tournamentPosition?: number | null;
}

/**
 * Factory class for creating IJoinedRoomState objects with proper defaults
 */
export class JoinedRoomStateFactory {
    
    // Helper functions
    private static toArray = (val: any) => Array.isArray(val) ? val : (val ? Array.from(val) : []);
    private static safeArray = (val: any) => Array.isArray(val) ? val : [];

    /**
     * Creates a new IJoinedRoomState for regular room joins (from lobby/server)
     */
    static createFromRoomJoin(data: {
        roomState: IRoomState | null;
        serverState: any;
        type: string;
        gameId: string;
        tournamentGameId?: string;
        name?: string;
        roomId: string;
        isTournament?: boolean;
    }): IJoinedRoomState {
        const { roomState, serverState, type, gameId, tournamentGameId, name, roomId, isTournament } = data;
        
        return {
            ...roomState,
            type,
            gameId,
            handId: serverState.handId,
            stateVersion: serverState.stateVersion,
            lastAppliedGameplaySequence: serverState.lastAppliedGameplaySequence,
            lastGameplayEventSequence: serverState.lastAppliedGameplaySequence,
            serverTime: serverState.serverTime,
            gameType: type || roomState?.gameType || 'texas',
            tournamentGameId: tournamentGameId || roomState?.tournamentId,
            activityMessages: [],
            playerSeatIndex: serverState?.playerSeatIndex ?? null,
            pot: serverState.pot ?? null,
            currentBet: serverState.currentBet ?? null,
            activePlayerId: serverState.activePlayerId ?? null,
            communityCards: serverState.communityCards || [],
            chatMessages: [],
            joinedAt: new Date().toLocaleString(),
            initialized: true,
            joinSource: 'lobby',
            spectators: serverState.spectators || {},
            players: serverState.players || [],
            stateName: serverState.stateName ?? null,
            dealerId: serverState.dealerId ?? null,
            showSitFailedNoMoneyAlert: false,
            deck: serverState.deck || [],
            winners: serverState.winners || [],
            nonWinners: serverState.nonWinners || [],
            showdownCards: serverState.showdownCards || [],
            showdown: serverState.showdown ?? false,
            playersActedThisRound: serverState.playersActedThisRound ?? 0,
            shouldEndRound: serverState.shouldEndRound ?? false,
            smallBlindId: serverState.smallBlindId ?? null,
            bigBlindId: serverState.bigBlindId ?? null,
            shouldShowdown: serverState.shouldShowdown ?? false,
            secondsUntilNextGame: serverState.secondsUntilNextGame ?? 0,
            availableActions: serverState.availableActions ?? null,
            playerTurnHistory: serverState.playerTurnHistory || [],
            roundName: serverState.roundName ?? null,
            consecutiveGamesCount: serverState.consecutiveGamesCount ?? 0,
            roundingDust: serverState.roundingDust ?? 0,
            potBeforeDistribution: serverState.potBeforeDistribution ?? 0,
            turnTime: serverState.turnTime ?? 0,
            currentTurnStartTime: serverState.currentTurnStartTime ?? null,
            playerSatCount: serverState.playerSatCount ?? 0,
            showSitOptionsModal: false,
            sitOptions: null,
            showLeaveSeatDialog: false,
            showLeaveRoomDialog: false,
            sidePots: serverState.sidePots || [],
            winnersPotDistribution: serverState.winnersPotDistribution || [],
            ante: serverState.ante ?? 0,
            pendingBalanceUpdates: serverState.pendingBalanceUpdates ?? null,
            topUpNextHandPlayers: new Set(serverState.topUpNextHandPlayers || []),
            topUpAllHandsPlayers: new Set(serverState.topUpAllHandsPlayers || []),
            willSitOutNextHandPlayers: new Set(serverState.willSitOutNextHandPlayers || []),
            playerEquities: serverState.playerEquities || {},
            isGameInfoExpanded: false,
            currencyType: serverState.currencyType || roomState?.currencyType || 'fiat',
            currency: serverState.currency || roomState?.currency || '',
            exchangeCurrencyValue: serverState.exchangeCurrencyValue || roomState?.exchangeCurrencyValue || 0,
            secondsBeforeRemovingSitOutPlayers: serverState.secondsBeforeRemovingSitOutPlayers ?? 0,
            stakeLevel: serverState.stakeLevel || roomState?.stakeLevel || '',
            visiblePot: serverState.visiblePot ?? 0,
            currentGameId: serverState.currentGameId || '',
            eligiblePotPlayers: serverState.eligiblePotPlayers || [],
            visibleSidePots: serverState.visibleSidePots || [],
            gameId_: gameId,
            willSitOutNextBbPlayers: serverState.willSitOutNextBbPlayers || [],
            playersWaitingForBB: serverState.playersWaitingForBB || [],
            smallBlind: serverState.smallBlind ?? roomState?.smallBlind ?? 0,
            bigBlind: serverState.bigBlind ?? roomState?.bigBlind ?? 0,
            minBuyIn: serverState.minBuyIn ?? roomState?.minBuyIn ?? 0,
            maxBuyIn: serverState.maxBuyIn ?? roomState?.maxBuyIn ?? 0,
            name: name || roomState?.name || '',
            roomId,
            isTournamentTable: isTournament ?? roomState?.isTournamentTable ?? false,
            isFinalTournamentTable: serverState.isFinalTournamentTable ?? false,
            minPlayers: serverState.minPlayers ?? roomState?.minPlayers ?? 2,
            maxPlayers: serverState.maxPlayers ?? roomState?.maxPlayers ?? 9,
            isActive: serverState.isActive ?? roomState?.isActive ?? true,
            isPausedForTableOperation: serverState.isPausedForTableOperation ?? false,
            isTableClosed: serverState.isTableClosed ?? roomState?.isTableClosed ?? false,
            addonEnabled: serverState.addonEnabled ?? false,
            addonEndsAt: serverState.addonEndsAt ?? null,
            isTournamentStarting: serverState.isTournamentStarting ?? roomState?.isTournamentStarting ?? false,
        } as IJoinedRoomState;
    }

    /**
     * Creates a new IJoinedRoomState from server request (joinInClient/tournament moves)
     */
    static createFromServerRequest(newRoomState: Partial<IJoinedRoomState>): IJoinedRoomState {
        if (!newRoomState.roomId) {
            throw new Error('createFromServerRequest: roomId is required');
        }

        const rawGameType = (newRoomState as any).gameType || (newRoomState as any).type || 'texas';
        let normalizedType = (newRoomState as any).type || (newRoomState as any).gameType || 'texas';
        if (normalizedType === 'tournament') {
            if (rawGameType && rawGameType !== 'tournament') {
                normalizedType = rawGameType;
            } else if ((newRoomState as any).isTournamentTable) {
                normalizedType = 'texas';
            }
        }
        const normalizedGameType = rawGameType === 'tournament' ? normalizedType : rawGameType;

        const gameId = newRoomState.gameId;
        if (!gameId) {
            throw new Error('createFromServerRequest: gameId is required');
        }

        // Ensure tournament detection is properly set
        const isTournamentTable = newRoomState.isTournamentTable ?? 
                                 Boolean(newRoomState.tournamentGameId || newRoomState.tournamentId);

        return {
            ...newRoomState,
            initialized: true,
            joinedAt: new Date().toLocaleString(),
            handId: newRoomState.handId,
            stateVersion: newRoomState.stateVersion,
            lastAppliedGameplaySequence: newRoomState.lastAppliedGameplaySequence,
            lastGameplayEventSequence: newRoomState.lastAppliedGameplaySequence,
            serverTime: newRoomState.serverTime,
            // Preserve participant indicators if present; required for "dock vs leave" decisions when closing detached windows.
            playerSeatIndex: newRoomState.playerSeatIndex ?? null,
            pot: newRoomState.pot ?? null,
            currentBet: newRoomState.currentBet ?? null,
            activePlayerId: newRoomState.activePlayerId ?? null,
            communityCards: newRoomState.communityCards || [],
            stateName: newRoomState.stateName ?? null,
            roundName: newRoomState.roundName ?? null,
            dealerId: newRoomState.dealerId ?? null,
            showSitFailedNoMoneyAlert: false,
            deck: newRoomState.deck || [],
            winners: newRoomState.winners || [],
            nonWinners: newRoomState.nonWinners || [],
            showdownCards: newRoomState.showdownCards || [],
            showdown: newRoomState.showdown ?? false,
            playersActedThisRound: newRoomState.playersActedThisRound ?? 0,
            shouldEndRound: newRoomState.shouldEndRound ?? false,
            smallBlindId: newRoomState.smallBlindId ?? null,
            bigBlindId: newRoomState.bigBlindId ?? null,
            shouldShowdown: newRoomState.shouldShowdown ?? false,
            secondsUntilNextGame: newRoomState.secondsUntilNextGame ?? 0,
            availableActions: newRoomState.availableActions ?? null,
            playerTurnHistory: this.safeArray(newRoomState.playerTurnHistory),
            consecutiveGamesCount: newRoomState.consecutiveGamesCount ?? 0,
            roundingDust: newRoomState.roundingDust ?? 0,
            potBeforeDistribution: newRoomState.potBeforeDistribution ?? 0,
            turnTime: newRoomState.turnTime ?? 0,
            currentTurnStartTime: newRoomState.currentTurnStartTime ?? null,
            playerSatCount: newRoomState.playerSatCount ?? 0,
            showSitOptionsModal: false,
            sitOptions: null,
            showLeaveSeatDialog: false,
            showLeaveRoomDialog: false,
            sidePots: newRoomState.sidePots || [],
            winnersPotDistribution: newRoomState.winnersPotDistribution || [],
            ante: newRoomState.ante ?? 0,
            pendingBalanceUpdates: newRoomState.pendingBalanceUpdates ?? null,
            topUpNextHandPlayers: this.toArray(newRoomState.topUpNextHandPlayers),
            topUpAllHandsPlayers: this.toArray(newRoomState.topUpAllHandsPlayers),
            willSitOutNextHandPlayers: this.toArray(newRoomState.willSitOutNextHandPlayers),
            willSitOutNextBbPlayers: this.toArray(newRoomState.willSitOutNextBbPlayers),
            playersWaitingForBB: this.toArray(newRoomState.playersWaitingForBB),
            activityMessages: this.safeArray(newRoomState.activityMessages),
            players: this.safeArray(newRoomState.players),
            chatMessages: this.safeArray(newRoomState.chatMessages),
            playerEquities: newRoomState.playerEquities || {},
            isGameInfoExpanded: false,
            currencyType: newRoomState.currencyType || (isTournamentTable ? 'tournament_chips' : 'fiat'),
            currency: newRoomState.currency || '',
            exchangeCurrencyValue: newRoomState.exchangeCurrencyValue || 0,
            secondsBeforeRemovingSitOutPlayers: newRoomState.secondsBeforeRemovingSitOutPlayers ?? 0,
            stakeLevel: newRoomState.stakeLevel || '',
            visiblePot: newRoomState.visiblePot ?? 0,
            currentGameId: newRoomState.currentGameId || '',
            eligiblePotPlayers: newRoomState.eligiblePotPlayers || [],
            visibleSidePots: newRoomState.visibleSidePots || [],
            gameId_: newRoomState.gameId_,
            isFinalTournamentTable: newRoomState.isFinalTournamentTable ?? false,
            isPausedForTableOperation: newRoomState.isPausedForTableOperation ?? false,
            addonEnabled: newRoomState.addonEnabled ?? false,
            addonEndsAt: newRoomState.addonEndsAt ?? null,
            isTableClosed: newRoomState.isTableClosed ?? false,
            isTournamentStarting: newRoomState.isTournamentStarting ?? false,
            spectators: newRoomState.spectators || {},
            gameId: gameId,
            roomId: newRoomState.roomId,
            name: newRoomState.name || `Table ${newRoomState.roomId}`,
            gameType: normalizedGameType,
            type: normalizedType,
            smallBlind: newRoomState.smallBlind ?? 0,
            bigBlind: newRoomState.bigBlind ?? 0,
            minBuyIn: newRoomState.minBuyIn ?? 0,
            maxBuyIn: newRoomState.maxBuyIn ?? 0,
            minPlayers: newRoomState.minPlayers ?? 2,
            maxPlayers: newRoomState.maxPlayers ?? 9,
            isActive: newRoomState.isActive ?? true,
            isTournamentTable: isTournamentTable,
            joinSource: 'server_request',
        } as IJoinedRoomState;
    }

    /**
     * Creates a new IJoinedRoomState with basic defaults (for addJoinedRoom)
     */
    static createWithDefaults(data: Partial<IJoinedRoomState>): IJoinedRoomState {
        return {
            ...data,
            initialized: true,
            joinedAt: new Date().toLocaleString(),
            handId: data.handId,
            stateVersion: data.stateVersion,
            lastAppliedGameplaySequence: data.lastAppliedGameplaySequence,
            lastGameplayEventSequence: data.lastAppliedGameplaySequence,
            serverTime: data.serverTime,
            playerSeatIndex: data.playerSeatIndex ?? null,
            pot: null,
            currentBet: null,
            activePlayerId: null,
            communityCards: [],
            stateName: null,
            roundName: null,
            dealerId: null,
            showSitFailedNoMoneyAlert: false,
            deck: [],
            winners: [],
            nonWinners: [],
            showdown: false,
            playersActedThisRound: 0,
            shouldEndRound: false,
            smallBlindId: null,
            bigBlindId: null,
            shouldShowdown: false,
            secondsUntilNextGame: 0,
            availableActions: null,
            playerTurnHistory: this.safeArray(data.playerTurnHistory),
            consecutiveGamesCount: 0,
            roundingDust: 0,
            potBeforeDistribution: 0,
            turnTime: 0,
            currentTurnStartTime: null,
            playerSatCount: 0,
            showSitOptionsModal: false,
            sitOptions: null,
            showLeaveSeatDialog: false,
            showLeaveRoomDialog: false,
            sidePots: [],
            winnersPotDistribution: [],
            ante: 0,
            pendingBalanceUpdates: null,
            topUpNextHandPlayers: this.toArray(data.topUpNextHandPlayers),
            topUpAllHandsPlayers: this.toArray(data.topUpAllHandsPlayers),
            willSitOutNextHandPlayers: this.toArray(data.willSitOutNextHandPlayers),
            willSitOutNextBbPlayers: this.toArray(data.willSitOutNextBbPlayers),
            playersWaitingForBB: this.toArray(data.playersWaitingForBB),
            activityMessages: this.safeArray(data.activityMessages),
            players: this.safeArray(data.players),
            chatMessages: this.safeArray(data.chatMessages),
            playerEquities: data.playerEquities || {},
            isGameInfoExpanded: false,
            spectators: data.spectators || {},
            secondsBeforeRemovingSitOutPlayers: 0,
            currencyType: data.currencyType || 'fiat',
            currency: data.currency || '',
            exchangeCurrencyValue: data.exchangeCurrencyValue || 0,
            stakeLevel: data.stakeLevel || '',
            visiblePot: 0,
            currentGameId: '',
            eligiblePotPlayers: [],
            visibleSidePots: [],
            isPausedForTableOperation: false,
            isTableClosed: false,
            isTournamentStarting: false,
            addonEnabled: false,
            addonEndsAt: null,
            gameId: data.gameId || '',
            roomId: data.roomId || '',
            name: data.name || '',
            gameType: data.gameType || 'texas',
            type: data.type || 'texas',
            smallBlind: data.smallBlind ?? 0,
            bigBlind: data.bigBlind ?? 0,
            minBuyIn: data.minBuyIn ?? 0,
            maxBuyIn: data.maxBuyIn ?? 0,
            minPlayers: data.minPlayers ?? 2,
            maxPlayers: data.maxPlayers ?? 9,
            isActive: data.isActive ?? true,
            isTournamentTable: data.isTournamentTable ?? 
                             Boolean(data.tournamentGameId || data.tournamentId),
        } as IJoinedRoomState;
    }
}
